import * as actionTypes from './actionTypes';
import { SiteImageActionTypes } from './actionTypes';
import { HttpError } from '../../config/Axios/axios-instance';
import { SiteImage } from '../../domain/SiteImage';

export type SiteImageStateType = {
  siteImages: SiteImage[] | null;
  siteImagesLoading: boolean;
  siteImagesError: HttpError;
  updatingSiteImageKey: string | null;
  siteImageUpdateError: HttpError;
  updatingCtaSiteImageKey: string | null;
  siteImageCtaUpdateError: HttpError;
  deletingSiteImageKey: string | null;
  siteImageDeleteError: HttpError;
};

export type SiteImageActionType = SiteImageStateType & {
  type: SiteImageActionTypes;
  siteImages: SiteImage[];
  updatedSiteImage: SiteImage;
};

export const initialState: SiteImageStateType = {
  siteImages: null,
  siteImagesLoading: true,
  siteImagesError: null,
  updatingSiteImageKey: null,
  siteImageUpdateError: null,
  updatingCtaSiteImageKey: null,
  siteImageCtaUpdateError: null,
  deletingSiteImageKey: null,
  siteImageDeleteError: null,
};

const fetchSiteImagesStart = (
  state: SiteImageStateType,
): SiteImageStateType => ({
  ...state,
  siteImagesLoading: true,
});

const fetchSiteImagesSuccess = (
  state: SiteImageStateType,
  action: SiteImageActionType,
): SiteImageStateType => ({
  ...state,
  siteImages: action.siteImages,
  siteImagesLoading: false,
  siteImagesError: null,
});

const fetchSiteImagesFail = (
  state: SiteImageStateType,
  action: SiteImageActionType,
): SiteImageStateType => ({
  ...state,
  siteImagesError: action.siteImagesError,
  siteImagesLoading: false,
});

const replaceSiteImage = (
  siteImages: SiteImage[] | null,
  updatedSiteImage: SiteImage,
): SiteImage[] | null =>
  siteImages?.map((siteImage) =>
    siteImage.key === updatedSiteImage.key ? updatedSiteImage : siteImage,
  ) ?? siteImages;

const updateSiteImageStart = (
  state: SiteImageStateType,
  action: SiteImageActionType,
): SiteImageStateType => ({
  ...state,
  updatingSiteImageKey: action.updatingSiteImageKey,
  siteImageUpdateError: null,
});

const updateSiteImageSuccess = (
  state: SiteImageStateType,
  action: SiteImageActionType,
): SiteImageStateType => ({
  ...state,
  updatingSiteImageKey: null,
  siteImageUpdateError: null,
  siteImages: replaceSiteImage(state.siteImages, action.updatedSiteImage),
});

const updateSiteImageFail = (
  state: SiteImageStateType,
  action: SiteImageActionType,
): SiteImageStateType => ({
  ...state,
  updatingSiteImageKey: null,
  siteImageUpdateError: action.siteImageUpdateError,
});

const updateSiteImageCtaStart = (
  state: SiteImageStateType,
  action: SiteImageActionType,
): SiteImageStateType => ({
  ...state,
  updatingCtaSiteImageKey: action.updatingCtaSiteImageKey,
  siteImageCtaUpdateError: null,
});

const updateSiteImageCtaSuccess = (
  state: SiteImageStateType,
  action: SiteImageActionType,
): SiteImageStateType => ({
  ...state,
  updatingCtaSiteImageKey: null,
  siteImageCtaUpdateError: null,
  siteImages: replaceSiteImage(state.siteImages, action.updatedSiteImage),
});

const updateSiteImageCtaFail = (
  state: SiteImageStateType,
  action: SiteImageActionType,
): SiteImageStateType => ({
  ...state,
  updatingCtaSiteImageKey: null,
  siteImageCtaUpdateError: action.siteImageCtaUpdateError,
});

const deleteSiteImageStart = (
  state: SiteImageStateType,
  action: SiteImageActionType,
): SiteImageStateType => ({
  ...state,
  deletingSiteImageKey: action.deletingSiteImageKey,
  siteImageDeleteError: null,
});

const deleteSiteImageSuccess = (
  state: SiteImageStateType,
  action: SiteImageActionType,
): SiteImageStateType => ({
  ...state,
  deletingSiteImageKey: null,
  siteImageDeleteError: null,
  siteImages: replaceSiteImage(state.siteImages, action.updatedSiteImage),
});

const deleteSiteImageFail = (
  state: SiteImageStateType,
  action: SiteImageActionType,
): SiteImageStateType => ({
  ...state,
  deletingSiteImageKey: null,
  siteImageDeleteError: action.siteImageDeleteError,
});

const resetSiteImageStore = (): SiteImageStateType => ({
  ...initialState,
});

const reducer = (state = initialState, action: SiteImageActionType) => {
  switch (action.type) {
    case actionTypes.FETCH_SITE_IMAGES_START:
      return fetchSiteImagesStart(state);
    case actionTypes.FETCH_SITE_IMAGES_SUCCESS:
      return fetchSiteImagesSuccess(state, action);
    case actionTypes.FETCH_SITE_IMAGES_FAIL:
      return fetchSiteImagesFail(state, action);
    case actionTypes.UPDATE_SITE_IMAGE_START:
      return updateSiteImageStart(state, action);
    case actionTypes.UPDATE_SITE_IMAGE_SUCCESS:
      return updateSiteImageSuccess(state, action);
    case actionTypes.UPDATE_SITE_IMAGE_FAIL:
      return updateSiteImageFail(state, action);
    case actionTypes.UPDATE_SITE_IMAGE_CTA_START:
      return updateSiteImageCtaStart(state, action);
    case actionTypes.UPDATE_SITE_IMAGE_CTA_SUCCESS:
      return updateSiteImageCtaSuccess(state, action);
    case actionTypes.UPDATE_SITE_IMAGE_CTA_FAIL:
      return updateSiteImageCtaFail(state, action);
    case actionTypes.DELETE_SITE_IMAGE_START:
      return deleteSiteImageStart(state, action);
    case actionTypes.DELETE_SITE_IMAGE_SUCCESS:
      return deleteSiteImageSuccess(state, action);
    case actionTypes.DELETE_SITE_IMAGE_FAIL:
      return deleteSiteImageFail(state, action);
    case actionTypes.RESET_SITE_IMAGE_STORE:
      return resetSiteImageStore();
    case actionTypes.LOGOUT:
      return resetSiteImageStore();
    default:
      return state;
  }
};

export default reducer;
